import { useState, useRef } from 'react';
// @ts-expect-error - TS2305 - Module '"@apollo/react-hooks"' has no exported member 'ApolloError'.
import { type QueryLazyOptions, useLazyQuery, type ApolloError } from '@apollo/react-hooks';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { handleApolloError } from '@atlassian/jira-issue-create-commons/src/common/utils/handle-apollo-error/index.tsx';
import type {
	QueryVariables,
	CreateConfigQueryResponseInContext,
	FetchConfigData,
	CreateConfigQueryResponseInContextWithML,
} from '../../common/types/index.tsx';
import {
	transformQueryResults,
	tranformModalLoadDataInContext,
} from '../../common/utils/fetch-config-query/index.tsx';
import { useSmartFieldsMachineLearning } from '../../common/utils/use-smart-fields-ml/index.tsx';
import { getCreateConfigQuery } from '../common/gql.tsx';
import { getCreateConfigUserPreferredQueryInContextCLI } from './gql-old/gql.tsx';
import { CREATE_CONFIG_USER_PREFERRED_QUERY_IN_CONTEXT } from './gql-old/index.tsx';
import { getCreateConfigUserPreferredQueryInContext } from './gql.tsx';
import type { UseFetchCreateConfigQueryInContext } from './types.tsx';

const useFetchCreateConfigQueryInContext: UseFetchCreateConfigQueryInContext = (props = {}) => {
	const { displayOnlyRequiredFields = false, triggerPointKey, isCreateLinkedIssue = false } = props;
	const [loading, setLoading] = useState(false);
	const [fetchError, setFetchError] = useState<ApolloError | null>(null);
	const apiStartTime = useRef<number>(0);
	const [apiLatency, setApiLatency] = useState<number>(0);
	const [data, setData] = useState<FetchConfigData>({
		fieldTabs: [],
		fieldConfig: {},
		contextFields: {},
		meta: {
			areRequiredFieldsSupported: true,
			queryErrorMessage: '',
		},
		additionalServices: {},
	});

	const { withMachineLearning } = fg('migrate_gic_smart_fields_jira_labs')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSmartFieldsMachineLearning()
		: { withMachineLearning: false };
	const oldFetchConfig = fg('create_linked_issues_trigger_point_issue_view')
		? getCreateConfigUserPreferredQueryInContextCLI(isCreateLinkedIssue)
		: CREATE_CONFIG_USER_PREFERRED_QUERY_IN_CONTEXT;

	const createConfigQuery = fg('new-issue-create-fetch-config-query')
		? getCreateConfigQuery({
				isInContext: true,
				withML: withMachineLearning,
				isCreateLinkedIssue,
				isCreateLinkedIssueFromIssueViewFGOn: !!fg('create_linked_issues_trigger_point_issue_view'),
				isJsmRespondersFieldEnabled: fg('jsm-responders-field-in-gicx-ui'),
			})
		: getCreateConfigUserPreferredQueryInContext(withMachineLearning, isCreateLinkedIssue);

	const [fetchConfigInContext] = useLazyQuery<
		CreateConfigQueryResponseInContext | CreateConfigQueryResponseInContextWithML,
		QueryVariables
	>(fg('migrate_gic_smart_fields_jira_labs') ? createConfigQuery : oldFetchConfig, {
		fetchPolicy: 'no-cache',
		onCompleted: (queryData) => {
			setApiLatency(Math.floor(performance.now() - apiStartTime.current));
			setData(
				transformQueryResults(tranformModalLoadDataInContext(queryData), displayOnlyRequiredFields),
			);
			setLoading(false);
		},
		onError: (error: ApolloError) => {
			setFetchError(error);
			fireErrorAnalytics({
				meta: {
					id: 'useFetchCreateConfigQueryInContext',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
				error,
				attributes: {
					// TODO: clean up after rollout of FG issue-create-fire-error-analytics-refactor
					errorType: handleApolloError(error),
					// TODO: look to get this into common hook and populate it everywhere.
					triggerPointKey,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		},
	});
	const handleFetch = useRef((queryVariables?: QueryLazyOptions<QueryVariables>) => {
		setLoading(true);
		apiStartTime.current = performance.now();
		fetchConfigInContext(queryVariables);
	});
	return [
		{
			isLoading: loading,
			fieldTabs: data.fieldTabs,
			fieldConfig: data.fieldConfig,
			contextFields: data.contextFields,
			meta: data.meta,
			error: fetchError,
			apiLatency,
			additionalServices: data.additionalServices,
		},
		handleFetch.current,
	];
};
export default useFetchCreateConfigQueryInContext;
